import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Download,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Search,
  Users,
  DollarSign,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { getDetailBatchPayment } from "@/api/Admin/Profit";
import Spinner from "@/components/Spinner";

type BatchStatus = "draft" | "processing" | "completed" | "failed";
type PaymentStatus = "pending" | "created" | "paid" | "failed";
type PackageType = "TRADER" | "PIONEER" | "TITAN";

type PaymentDetail = {
  id: string;
  investorName: string;
  investorCode: string;
  packageName: string;
  packageCode: string;
  packageType: string;
  paymentPeriod: string;
  grossAmount: number;
  taxAmount: number;
  netAmount: number;
  dueDate: string;
  status: PaymentStatus;
};

type BatchDetail = {
  id: string;
  code: string;
  name: string;
  month: string;
  status: BatchStatus;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  note?: string;
  totalInvestors: number;
  totalPayments: number;
  totalGross: number;
  totalTax: number;
  totalNet: number;
  payments: PaymentDetail[];
};

const BatchDetailPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  // const navigate = useNavigate();
  const { t } = useTranslation();
  const [batchDetail, setBatchDetail] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [packageTypeFilter, setPackageTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!batchId) return;
      setLoading(true);
      try {
        const res = await getDetailBatchPayment({
          id: parseInt(batchId),
          search: searchTerm,
          status: statusFilter,
          package: packageTypeFilter,
          limit: 100,
          page: 1,
        });

        if (res?.data) {
          const detail = res.data;
          const mappedBatch: BatchDetail = {
            id: detail.info.id.toString(),
            code: `BATCH-${detail.info.id}`,
            name: detail.info.title,
            month: detail.info.month,
            status: detail.info.status as BatchStatus,
            createdBy: "Admin",
            createdAt: "N/A",
            totalInvestors: new Set(detail.list.data.map(p => p.user_id)).size,
            totalPayments: detail.list.total,
            totalGross: parseFloat(detail.summary.total_gross_amount),
            totalTax: parseFloat(detail.summary.total_tax_fee),
            totalNet: parseFloat(detail.summary.total_net_amount),
            payments: detail.list.data.map((p: any) => ({
              id: p.id.toString(),
              investorName: p.user_info?.fullname || "N/A",
              investorCode: p.user_info?.cccd_number || `UID${p.user_id}`,
              packageName: p.package_name,
              packageCode: "N/A",
              packageType: "N/A",
              paymentPeriod: p.period,
              grossAmount: parseFloat(p.gross_amount),
              taxAmount: parseFloat(p.tax_fee),
              netAmount: parseFloat(p.net_amount),
              dueDate: p.due_date,
              status: p.status as PaymentStatus,
            })),
          };
          setBatchDetail(mappedBatch);
        }
      } catch (error) {
        console.error("Error fetching batch detail:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [batchId, searchTerm, packageTypeFilter, statusFilter]);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("batchDetail.breadcrumbHistory"), path: "/admin/batch-history" },
    { label: t("batchDetail.breadcrumbDetail") },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + " đ";
  };

  const getBatchStatusBadge = (status: BatchStatus) => {
    const badges = {
      draft: {
        bg: "bg-gray-100 dark:bg-gray-700",
        text: "text-gray-700 dark:text-gray-300",
        icon: FileText,
        label: t("batchHistory.statusDraft"),
      },
      processing: {
        bg: "bg-blue-100 dark:bg-blue-900/30",
        text: "text-blue-700 dark:text-blue-400",
        icon: Clock,
        label: t("batchHistory.statusProcessing"),
      },
      completed: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        icon: CheckCircle,
        label: t("batchHistory.statusCompleted"),
      },
      failed: {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-400",
        icon: XCircle,
        label: t("batchHistory.statusFailed"),
      },
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const badges = {
      pending: {
        bg: "bg-gray-100 dark:bg-gray-700",
        text: "text-gray-700 dark:text-gray-300",
        dot: "bg-gray-500",
        label: t("batchDetail.paymentPending"),
      },
      created: {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-400",
        dot: "bg-yellow-500",
        label: t("batchDetail.paymentCreated"),
      },
      paid: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        dot: "bg-green-500",
        label: t("batchDetail.paymentPaid"),
      },
      failed: {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-400",
        dot: "bg-red-500",
        label: t("batchDetail.paymentFailed"),
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        <span className={`w-2 h-2 rounded-full ${badge.dot}`}></span>
        {badge.label}
      </span>
    );
  };

  const handleExport = () => {
    // api export batch report
    console.log("Exporting batch:", batchId);
  };

  if (loading && !batchDetail) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Spinner />
        <p className="mt-4 text-gray-500">{t("common.loading")}</p>
      </div>
    );
  }

  if (!batchDetail) {
    return (
      <div className="p-10 text-center">
        <p className="text-red-500 font-bold">Batch not found or error occurred.</p>
      </div>
    );
  }

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{batchDetail.name} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("batchDetail.title")}
        description=""
        breadcrumbs={breadcrumbs}
      />

      {/* Batch Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {batchDetail.name}
          </h2>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {batchDetail.code}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Tháng {batchDetail.month}
            </span>
            {getBatchStatusBadge(batchDetail.status)}
          </div>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-md font-medium transition-colors text-sm"
        >
          <Download className="w-4 h-4" />
          {t("batchDetail.exportExcel")}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("batchDetail.totalInvestors")}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {batchDetail.totalInvestors}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-md">
              <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("batchDetail.totalPayments")}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {batchDetail.totalPayments}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-md">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("batchDetail.totalAmount")}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(batchDetail.totalNet)}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-md">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("batchDetail.createdAt")}
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                {batchDetail.createdAt}
              </p>
            </div>
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-md">
              <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t("batchDetail.paymentSummary")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-700/50 rounded-md p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("batchDetail.totalGross")}
            </p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(batchDetail.totalGross)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-700/50 rounded-md p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("batchDetail.totalTax")}
            </p>
            <p className="text-2xl font-bold text-red-500">
              {formatCurrency(batchDetail.totalTax)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-700/50 rounded-md p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {t("batchDetail.totalNet")}
            </p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(batchDetail.totalNet)}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t("batchDetail.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
          </div>
        </div>
        <div>
          <select
            value={packageTypeFilter}
            onChange={(e) => setPackageTypeFilter(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("batchDetail.allPackageTypes")}</option>
            <option value="TRADER">Trader</option>
            <option value="PIONEER">Pioneer</option>
            <option value="TITAN">Titan</option>
          </select>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("batchDetail.allStatuses")}</option>
            <option value="pending">{t("batchDetail.paymentPending")}</option>
            <option value="created">{t("batchDetail.paymentCreated")}</option>
            <option value="paid">{t("batchDetail.paymentPaid")}</option>
            <option value="failed">{t("batchDetail.paymentFailed")}</option>
          </select>
        </div>
      </div>

      {/* Payment Detail Table */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("batchDetail.paymentList")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("batchDetail.totalPaymentsCount", { count: batchDetail.totalPayments })}
              </p>
            </div>
            {loading && <Spinner size={24} />}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.stt")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.investor")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.packageCode")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.paymentPeriod")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.grossAmount")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.tax")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.netAmount")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.dueDate")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  {t("batchDetail.status")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {batchDetail.payments.map((payment, index) => (
                <tr
                  key={payment.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {index + 1}
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {payment.investorName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {payment.investorCode}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-900 dark:text-white">
                      {payment.packageName}
                    </p>
                    <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {payment.packageCode}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-gray-700 dark:text-gray-300">
                    {payment.paymentPeriod}
                  </td>
                  <td className="px-4 py-4 text-right text-sm text-gray-700 dark:text-gray-300">
                    {formatCurrency(payment.grossAmount)}
                  </td>
                  <td className="px-4 py-4 text-right text-sm text-red-500">
                    {formatCurrency(payment.taxAmount)}
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(payment.netAmount)}
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-gray-700 dark:text-gray-300 font-mono">
                    {payment.dueDate}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {getPaymentStatusBadge(payment.status)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-700/50 font-semibold">
                <td colSpan={4} className="px-4 py-4 text-sm text-gray-900 dark:text-white text-right">
                  {t("batchDetail.total")}
                </td>
                <td className="px-4 py-4 text-right text-sm text-gray-900 dark:text-white">
                  {formatCurrency(batchDetail.totalGross)}
                </td>
                <td className="px-4 py-4 text-right text-sm text-red-500">
                  {formatCurrency(batchDetail.totalTax)}
                </td>
                <td className="px-4 py-4 text-right text-sm text-green-600 dark:text-green-400">
                  {formatCurrency(batchDetail.totalNet)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BatchDetailPage;
